import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';
import { AdminCampaignCard } from './AdminCampaignCard';
import { CampaignDetailsModal } from '../CampaignDetailsModal';
import { TeamCampaignOverview } from './TeamCampaignOverview';
import { buildActiveTeamMembers, normalizeManagerId, resolveCampaignOwner } from './teamOwnership';
import { resolveCampaignContextName } from '../../utils/sellerReference';
import { normalizeCampaignLifecycleStatus } from '../../utils/campaignLifecycleStatus';
import type { CampaignMetadata } from '../../types';
import type { TeamInvitationData, TeamMemberData } from '../../types/n8nResponses';

type CampaignStatusFilter = 'all' | 'active' | 'paused' | 'archived';
type TeamViewMode = 'list' | 'compact';

interface AdminCampaignBoardProps {
  campaigns: CampaignMetadata[];
  teamMembers: TeamMemberData[];
  teamInvitations: TeamInvitationData[];
  isLoading: boolean;
  onOpenCampaign?: (campaignId: string) => void;
  onViewMembers?: () => void;
  onViewSellers?: () => void;
  sellerCount?: number;
  /** Team's current AI-credit balance — forwarded to the overview's runway pill. */
  availableCredits?: number | null;
  /** Opens the Buy-Credits modal — surfaced in the pill when coverage runs low. */
  onAddCredits?: () => void;
  /** Team name — fallback "seller" for campaigns without a seller assigned. */
  teamName?: string | null;
  /** Team's `id§Name` seller registry — resolves bare campaign seller IDs. */
  sellerRegistry?: string | null;
  /** Opens the Report Generator locked to a campaign (card "+Report"). */
  onGenerateReport?: (campaignId: string) => void;
  /** Authenticated (or impersonated) user email — forwarded to the overview
   *  so its pipeline drill-down can fetch each campaign's lead list. */
  userEmail?: string;
}

/**
 * Board-filter bucket for a campaign. Delegates to the SHARED lifecycle
 * normalizer so filtering can never disagree with the pill it filters —
 * "completed"/"done" surface under Active (finished work is still live work in
 * Team view) because the shared normalizer returns null for them.
 */
const normalizeCampaignStatus = (value: string | null | undefined): Exclude<CampaignStatusFilter, 'all'> =>
  normalizeCampaignLifecycleStatus(value) ?? 'active';

const needsExplicitAssignment = (campaign: CampaignMetadata) => !normalizeManagerId(campaign.account_manager_id);

const STATUS_FILTERS: Array<{
  key: CampaignStatusFilter;
  label: string;
  tone: string;
}> = [
  { key: 'all', label: 'All', tone: 'border-white/12 bg-white/[0.04] text-white/80' },
  { key: 'active', label: 'Active', tone: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-100' },
  { key: 'paused', label: 'Paused', tone: 'border-amber-400/20 bg-amber-400/10 text-amber-100' },
  { key: 'archived', label: 'Archived', tone: 'border-white/12 bg-white/[0.04] text-white/55' },
];

const SORT_LABELS: Record<string, string> = {
  relevance: 'Recommended', seller: 'Seller (grouped)', status: 'Status', created: 'Created date',
};

export const AdminCampaignBoard: React.FC<AdminCampaignBoardProps> = ({
  campaigns,
  teamMembers,
  teamInvitations,
  isLoading,
  onOpenCampaign,
  onViewMembers,
  onViewSellers,
  sellerCount,
  availableCredits,
  onAddCredits,
  teamName,
  sellerRegistry,
  onGenerateReport,
  userEmail = '',
}) => {
  const activeTeamMembers = useMemo(
    () => buildActiveTeamMembers(teamMembers, teamInvitations),
    [teamMembers, teamInvitations]
  );
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selectedCampaignForDetails, setSelectedCampaignForDetails] = useState<CampaignMetadata | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('all');
  // Default to Compact — the dense row view gives team admins the most
  // campaign-per-glance density when opening the Team view.
  const [viewMode, setViewMode] = useState<TeamViewMode>('compact');
  const [memberFilterOpen, setMemberFilterOpen] = useState(false);
  const [needsAssignmentOnly, setNeedsAssignmentOnly] = useState(false);
  const [selectedManagerIds, setSelectedManagerIds] = useState<Set<string>>(new Set());
  // Sort control (Manuel): Seller (grouped), Status, or Created date. The
  // relevance sort below stays as the base so "important" campaigns still lead
  // within equal sort keys (e.g. unsold/needs-assignment first inside a seller).
  type CampaignSort = 'relevance' | 'seller' | 'status' | 'created';
  const [sortBy, setSortBy] = useState<CampaignSort>('relevance');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortOpen, setSortOpen] = useState(false);

  const teamMemberFilterOptions = useMemo(
    () => activeTeamMembers.map((member) => ({
      id: member.user_id,
      label: member.name || member.email,
      sublabel: member.name ? member.email : undefined,
    })),
    [activeTeamMembers]
  );

  // SELLER eyebrow per campaign (Manuel): the seller name above the campaign
  // name; campaigns WITHOUT a seller fall back to the TEAM name. Bare seller
  // IDs ("71005") resolve through the team's `id§Name` registry. One shared
  // resolver (resolveCampaignContextName) so this can never drift from the
  // Campaigns page's identical fallback chain.
  const sellerLabels = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const campaign of campaigns) {
      map[campaign.id] = resolveCampaignContextName({
        sellerReference: campaign.seller_lvl1,
        sellerName: campaign.seller_name,
        teamName,
        registry: sellerRegistry,
      });
    }
    return map;
  }, [campaigns, teamName, sellerRegistry]);

  const filteredCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();

    return campaigns
      .filter((campaign) => {
        const normalizedStatus = normalizeCampaignStatus(campaign.status);
        const manager = resolveCampaignOwner(campaign, activeTeamMembers, campaign.client_email);
        const managerId = manager?.user_id || null;
        const selectedTeamMemberIds = Array.from(selectedManagerIds);

        if (statusFilter !== 'all' && normalizedStatus !== statusFilter) {
          return false;
        }

        if (needsAssignmentOnly && !needsExplicitAssignment(campaign)) {
          return false;
        }

        if (selectedTeamMemberIds.length > 0) {
          const includeAssignedMember = managerId ? selectedManagerIds.has(managerId) : false;
          if (!includeAssignedMember) {
            return false;
          }
        }

        if (!query) return true;

        return [
          campaign.name,
          campaign.client_email,
          campaign.client_name,
          campaign.search_location,
          manager?.name,
          manager?.email,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      })
      .sort((left, right) => {
        // Base relevance: active campaigns lead, needs-assignment next — keeps
        // the board's "what needs my attention" quality inside every sort.
        const leftStatus = normalizeCampaignStatus(left.status);
        const rightStatus = normalizeCampaignStatus(right.status);
        const leftActiveRank = leftStatus === 'active' ? 0 : 1;
        const rightActiveRank = rightStatus === 'active' ? 0 : 1;
        if (sortBy === 'relevance') {
          if (leftActiveRank !== rightActiveRank) return leftActiveRank - rightActiveRank;
          const leftNeedsRank = needsExplicitAssignment(left) ? 0 : 1;
          const rightNeedsRank = needsExplicitAssignment(right) ? 0 : 1;
          if (leftNeedsRank !== rightNeedsRank) return leftNeedsRank - rightNeedsRank;
          const leftUpdated = new Date(left.updatedAt || left.createdAt || left.created || 0).getTime();
          const rightUpdated = new Date(right.updatedAt || right.createdAt || right.created || 0).getTime();
          if (leftUpdated !== rightUpdated) return rightUpdated - leftUpdated;
          return left.name.localeCompare(right.name);
        }

        const direction = sortDirection === 'asc' ? 1 : -1;

        if (sortBy === 'seller') {
          // GROUPED by seller: campaigns sharing a seller sit together, seller
          // name resolved through the SAME resolver the card eyebrow uses, so
          // grouping can never disagree with what the user sees on the card.
          const leftSeller = sellerLabels[left.id] || '—';
          const rightSeller = sellerLabels[right.id] || '—';
          const bySeller = direction * leftSeller.localeCompare(rightSeller);
          if (bySeller !== 0) return bySeller;
          // Inside one seller: most recently updated first.
          const lu = new Date(left.updatedAt || left.createdAt || left.created || 0).getTime();
          const ru = new Date(right.updatedAt || right.createdAt || right.created || 0).getTime();
          if (lu !== ru) return ru - lu;
          return left.name.localeCompare(right.name);
        }

        if (sortBy === 'status') {
          // Active → paused → archived (pipeline order), then updated desc.
          const statusRank: Record<string, number> = { active: 0, paused: 1, archived: 2 };
          const byStatus = direction * ((statusRank[leftStatus] ?? 3) - (statusRank[rightStatus] ?? 3));
          if (byStatus !== 0) return byStatus;
          const lu = new Date(left.updatedAt || left.createdAt || left.created || 0).getTime();
          const ru = new Date(right.updatedAt || right.createdAt || right.created || 0).getTime();
          return lu !== ru ? ru - lu : left.name.localeCompare(right.name);
        }

        // Created date — the user's explicit asc/desc choice.
        const lc = new Date(left.createdAt || left.created || 0).getTime();
        const rc = new Date(right.createdAt || right.created || 0).getTime();
        if (lc !== rc) return (lc - rc) * direction;
        return left.name.localeCompare(right.name);
      });
  }, [campaigns, search, selectedManagerIds, statusFilter, needsAssignmentOnly, activeTeamMembers, sortBy, sortDirection, sellerLabels]);

  // OVERVIEW metrics mirror the BOARD SELECTION (Manuel): the snapshot card's
  // graphs aggregate the FILTERED set, so picking "Active" (or an owner /
  // search / needs-assignment filter) re-scopes every graph to that selection.
  // Placed after filteredCampaigns — these numbers derive from it.
  const overviewMetrics = useMemo(() => ({
    activeCampaigns: filteredCampaigns.filter((campaign) => normalizeCampaignStatus(campaign.status) === 'active').length,
    needsAssignmentCampaigns: filteredCampaigns.filter(needsExplicitAssignment).length,
  }), [filteredCampaigns]);

  const selectedMemberSummary = useMemo(() => {
    if (selectedManagerIds.size === 0) return 'All team members';

    const labels = teamMemberFilterOptions
      .filter((option) => selectedManagerIds.has(option.id))
      .map((option) => option.label);

    if (labels.length === 1) return labels[0];
    return `${labels.length} members selected`;
  }, [selectedManagerIds, teamMemberFilterOptions]);

  const hasActiveFilters = search.trim().length > 0 || statusFilter !== 'all' || selectedManagerIds.size > 0 || needsAssignmentOnly;

  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSelectedManagerIds(new Set());
    setNeedsAssignmentOnly(false);
    setSortBy('relevance');
    setSortDirection('desc');
    setMemberFilterOpen(false);
  };

  const focusAllCampaigns = () => {
    clearAllFilters();
  };

  const focusActiveCampaigns = () => {
    setSearch('');
    setStatusFilter('active');
    setSelectedManagerIds(new Set());
    setNeedsAssignmentOnly(false);
    setMemberFilterOpen(false);
  };

  const focusNeedsAssignment = () => {
    setSearch('');
    setStatusFilter('all');
    setSelectedManagerIds(new Set());
    setNeedsAssignmentOnly(true);
    setMemberFilterOpen(false);
  };

  const toggleManagerSelection = (managerId: string) => {
    setSelectedManagerIds((prev) => {
      const next = new Set(prev);
      if (next.has(managerId)) {
        next.delete(managerId);
      } else {
        next.add(managerId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-24">
        <div className="h-16 w-16 animate-spin rounded-full border-l-2 border-t-2 border-cyan-300 shadow-[0_0_24px_rgba(34,211,238,0.25)]" />
        <p className="animate-pulse text-xs font-black uppercase tracking-[0.28em] text-white/40">
          Loading team campaigns...
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Dashboard card — describes the CURRENT SELECTION: every graph
          aggregates the filtered set (Manuel), not the whole team. */}
      <section className="team-section-header relative z-20">
        <TeamCampaignOverview campaigns={filteredCampaigns} active={overviewMetrics.activeCampaigns}
          unassigned={overviewMetrics.needsAssignmentCampaigns} onAll={focusAllCampaigns}
          onActive={focusActiveCampaigns} onUnassigned={focusNeedsAssignment}
          availableCredits={availableCredits} onAddCredits={onAddCredits}
          userEmail={userEmail}
          sellerLabels={sellerLabels}
          teamMembers={activeTeamMembers.map((member, index) => ({
            name: member.name || member.email,
            email: member.email,
            // Stable per-member color from a fixed palette.
            color: ['#67E8F9', '#A78BFA', '#FBBF24', '#F97316', '#EC4899', '#34D399', '#94A3B8', '#60A5FA'][index % 8],
            count: filteredCampaigns.filter((campaign) => campaign.account_manager_id === member.user_id).length,
          }))} />
      </section>

      <section className="mt-4">
        {hasActiveFilters && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {search.trim() && (
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white/70">
                Search: {search.trim()}
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white/70">
                Status: {statusFilter}
              </span>
            )}
            {needsAssignmentOnly && (
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/16 bg-amber-300/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-amber-100">
                Needs assignment
              </span>
            )}
            {selectedManagerIds.size > 0 && (
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white/70">
                Owners: {selectedMemberSummary}
              </span>
            )}
            <button
              type="button"
              onClick={clearAllFilters}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-300/16 bg-cyan-300/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100 transition-all hover:border-cyan-300/28 hover:bg-cyan-300/14"
            >
              Clear Filters
            </button>
          </div>
        )}

        {filteredCampaigns.length === 0 ? (
          <div className="team-member-directory flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/8 bg-white/[0.03]">
              <Icon icon="solar:folder-error-bold" className="h-6 w-6 text-white/45" aria-hidden="true" />
            </div>
            <p className="mt-5 text-[11px] font-black uppercase tracking-[0.24em] text-white/30">
              {hasActiveFilters ? 'No campaigns match the current filters' : 'No campaigns available'}
            </p>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#8089A3]">
              Adjust your search, owner, assignment, or campaign status filters to widen the board.
            </p>
            {hasActiveFilters && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[14px] border border-cyan-300/18 bg-cyan-300/10 px-5 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100 transition-all hover:border-cyan-300/28 hover:bg-cyan-300/14"
                >
                  <Icon icon="solar:restart-bold" className="h-4 w-4" aria-hidden="true" />
                  Show All Campaigns
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="team-member-directory">
            {/* Toolbar INSIDE the list container — same as Members/Sellers.
                Search first (as before), then status filters. */}
            <div className="team-directory-toolbar">
              <div className="team-directory-search">
                <Icon icon="bx:search" aria-hidden="true" />
                <input
                  type="text"
                  aria-label="Search team campaigns"
                  placeholder="Search campaigns, clients, or owners..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="team-search"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {STATUS_FILTERS.map((filter) => {
                  const isActive = statusFilter === filter.key;
                  // CAMPAIGN count per filter — tells the user how many
                  // campaigns sit in each status bucket.
                  const campaignsInFilter = campaigns
                    .filter((campaign) => filter.key === 'all' || normalizeCampaignStatus(campaign.status) === filter.key)
                    .length;
                  return (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setStatusFilter(filter.key)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] transition-all ${
                        isActive
                          ? `${filter.key === 'all' ? 'border-white bg-white text-[#0B0B0D]' : filter.tone} shadow-[0_10px_22px_rgba(0,0,0,0.16)]`
                          : 'border-white/8 bg-white/[0.02] text-white/45 hover:border-white/14 hover:bg-white/[0.05] hover:text-white/72'
                      }`}
                    >
                      {filter.label}
                      <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-black tabular-nums ${isActive ? (filter.key === 'all' ? 'bg-black/10' : 'bg-black/25') : 'bg-white/[0.05]'}`}>
                        {campaignsInFilter.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="rounded-full border border-cyan-300/16 bg-cyan-300/10 px-3.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100 transition-all hover:border-cyan-300/28 hover:bg-cyan-300/14"
                  >
                    Show All
                  </button>
                )}
              </div>

              <div className="relative ml-auto flex items-center gap-2">
                {/* SORT — seller (grouped) / status / created date. asc-desc
                    applies to date + alphabetical sorts; status always sorts
                    pipeline order (active → paused → archived). */}
                <button
                  type="button"
                  onClick={() => setSortOpen((open) => !open)}
                  className="team-directory-select inline-flex min-h-9 w-auto items-center justify-between gap-2 px-3 text-[12px]"
                >
                  <span className="truncate">Sort: {SORT_LABELS[sortBy]}{sortBy !== 'status' ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                  <Icon icon="solar:alt-arrow-down-linear" className={`h-3.5 w-3.5 shrink-0 text-white/45 transition-transform ${sortOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {sortOpen && (
                  <div className="absolute right-0 top-[calc(100%+10px)] z-30 w-[240px] overflow-hidden rounded-[20px] border border-white/10 bg-[#090B10] shadow-[0_24px_60px_rgba(0,0,0,0.38)]">
                    <div className="border-b border-white/6 px-4 py-3">
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/55">Sort campaigns</div>
                    </div>
                    {(['relevance','seller','status','created'] as const).map(key => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          if (key === sortBy && key !== 'status') { setSortDirection(d => d === 'asc' ? 'desc' : 'asc'); }
                          else { setSortBy(key); setSortDirection(key === 'created' ? 'desc' : 'asc'); }
                          setSortOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-white/[0.05] ${sortBy === key ? 'text-white' : 'text-white/60'}`}
                      >
                        <span>{SORT_LABELS[key]}</span>
                        <span className="flex items-center gap-2">
                          {sortBy === key && key !== 'status' && key !== 'relevance' && (
                            <span className="font-mono text-[10px] text-white/45">{sortDirection === 'asc' ? '↑ asc' : '↓ desc'}</span>
                          )}
                          {sortBy === key && <Icon icon="solar:check-circle-bold" className="h-4 w-4 text-[#8791ff]" aria-hidden="true" />}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setMemberFilterOpen((open) => !open)}
                  className="team-directory-select inline-flex min-h-9 w-auto items-center justify-between gap-2 px-3 text-[12px]"
                >
                  <span className="truncate">Owners: {selectedMemberSummary}</span>
                  <Icon icon="solar:alt-arrow-down-linear" className={`h-3.5 w-3.5 shrink-0 text-white/45 transition-transform ${memberFilterOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {memberFilterOpen && (
                  <div className="absolute right-0 top-[calc(100%+10px)] z-30 w-full overflow-hidden rounded-[20px] border border-white/10 bg-[#090B10] shadow-[0_24px_60px_rgba(0,0,0,0.38)] sm:w-[320px]">
                    <div className="flex items-center justify-between border-b border-white/6 px-4 py-3">
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/55">Filter by owner</div>
                        <div className="mt-1 text-xs text-[#7F89A4]">Choose one or many team members.</div>
                      </div>
                      {selectedManagerIds.size > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedManagerIds(new Set())}
                          className="text-[10px] font-black uppercase tracking-[0.14em] text-cyan-300 transition-colors hover:text-cyan-200"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="max-h-[280px] overflow-y-auto p-2">
                      {teamMemberFilterOptions.map((option) => {
                        const isSelected = selectedManagerIds.has(option.id);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => toggleManagerSelection(option.id)}
                            className={`mb-2 flex w-full items-start justify-between gap-3 rounded-[16px] border px-3 py-3 text-left transition-all ${
                              isSelected
                                ? 'border-cyan-300/20 bg-cyan-300/10'
                                : 'border-white/6 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.04]'
                            }`}
                          >
                            <div className="min-w-0">
                              <p className={`truncate text-sm ${isSelected ? 'text-white' : 'text-white/82'}`}>{option.label}</p>
                              {option.sublabel && (
                                <p className="mt-1 truncate text-xs text-[#7F89A4]">{option.sublabel}</p>
                              )}
                            </div>
                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                              isSelected
                                ? 'border-cyan-300/40 bg-cyan-300/18 text-cyan-100'
                                : 'border-white/10 text-transparent'
                            }`}>
                              <Icon icon="solar:check-read-bold" className="h-3 w-3" aria-hidden="true" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="inline-flex h-9 shrink-0 rounded-[10px] border border-white/10 bg-white/[0.035] p-0.5">
                  {([
                    { key: 'list', label: 'List', icon: 'solar:document-text-bold' },
                    { key: 'compact', label: 'Compact', icon: 'solar:hamburger-menu-bold' },
                  ] as Array<{ key: TeamViewMode; label: string; icon: string }>).map((option) => {
                    const isActive = viewMode === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setViewMode(option.key)}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-[8px] px-2.5 text-[10px] font-black uppercase tracking-[0.12em] transition-all ${
                          isActive
                            ? 'bg-white/[0.09] text-white'
                            : 'text-gray-500 hover:bg-white/[0.04] hover:text-white'
                        }`}
                      >
                        <Icon icon={option.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={
              viewMode === 'compact'
                ? 'flex flex-col'
                : viewMode === 'list'
                  ? 'flex flex-col gap-4 p-4'
                  : 'grid grid-cols-1 gap-5 p-4 xl:grid-cols-2 2xl:grid-cols-3'
            }>
              {/* Column headers — same as Members/Sellers (desktop only) */}
              {viewMode === 'compact' && (
                <div className="team-member-row team-member-columns team-campaign-columns" aria-hidden="true">
                  <span>Campaign</span>
                  <span className="text-left">Status</span>
                  <span className="text-left">Metrics</span>
                  <span className="text-left">Assigned</span>
                  <span className="text-left">Actions</span>
                </div>
              )}
              {filteredCampaigns.map((campaign) => (
                <AdminCampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  teamMembers={activeTeamMembers}
                  variant={viewMode}
                  onOpenCampaign={onOpenCampaign}
                  onOpenDetails={setSelectedCampaignForDetails}
                  sellerLabel={sellerLabels[campaign.id]}
                  onGenerateReport={onGenerateReport}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <InviteTeamMemberModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        teamMembers={activeTeamMembers}
        teamInvitations={teamInvitations}
      />

      <CampaignDetailsModal
        campaign={selectedCampaignForDetails}
        onClose={() => setSelectedCampaignForDetails(null)}
      />
    </>
  );
};
